import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtPKR } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import StatusPill from "../components/StatusPill";
import { Plus, Search, FileText } from "lucide-react";
import LoanDialog from "../components/LoanDialog";
import LoanCreatedDialog from "../components/LoanCreatedDialog";
import AgreementDialog from "../components/AgreementDialog";
import PageTip from "../components/PageTip";
import { useShopSettings } from "../lib/useShopSettings";

export default function Loans() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [createdLoan, setCreatedLoan] = useState(null);
  const [agreementLoanId, setAgreementLoanId] = useState(null);
  const { shop } = useShopSettings();
  const postCreateAction = shop?.loan_post_create_action || "auto_agreement";

  const load = async () => {
    const { data } = await api.get("/loans");
    setRows(data);
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = rows.filter((l) => {
    if (filter !== "all") {
      if (filter === "overdue" && l.current_status !== "overdue") return false;
      if (filter === "active" && l.status !== "active") return false;
      if (filter === "closed" && l.status !== "closed") return false;
    }
    if (q) {
      const s = q.toLowerCase();
      return (
        l.product_name?.toLowerCase().includes(s) ||
        l.customer_name?.toLowerCase().includes(s) ||
        l.customer_mobile?.toLowerCase().includes(s) ||
        l.imei?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="loans-page">
      <PageTip
        id="loans"
        icon={FileText}
        text="Create a loan for your customer and define the installment plan. Agreement aur receipt automatically generate ho jate hain."
      />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Loans</h1>
          <p className="text-slate-500 mt-1">All installment sales across your customers.</p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setOpen(true)} data-testid="new-loan-btn">
          <Plus className="w-4 h-4 mr-1" /> New Loan
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search product, customer, IMEI" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="loans-search" />
          </div>
          <div className="flex gap-1.5">
            {["all", "active", "overdue", "closed"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize border transition ${
                  filter === f
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
                data-testid={`filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="text-sm text-slate-500 ml-auto">{filtered.length} loans</div>
        </div>
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Customer</th>
                <th className="text-left px-4 py-3 font-semibold">Product</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Salesman</th>
                <th className="text-right px-4 py-3 font-semibold">Sale</th>
                <th className="text-right px-4 py-3 font-semibold">Remaining</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Next due</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-500">No loans match.</td>
                </tr>
              )}
              {filtered.map((l) => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`loan-row-${l.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{l.customer_name}</div>
                    <div className="text-xs text-slate-500 tabular">{l.customer_mobile}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{l.product_name}</div>
                    <div className="text-xs text-slate-500">{l.installment_type} · {l.duration}×</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-slate-600" data-testid={`loan-salesman-${l.id}`}>
                    {l.salesman_name || <span className="text-slate-400 italic">Unassigned</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular">{fmtPKR(l.sale_price)}</td>
                  <td className="px-4 py-3 text-right tabular font-semibold">{fmtPKR(l.remaining_amount)}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs tabular text-slate-600">
                    {l.next_due ? `${l.next_due.due_date} · ${fmtPKR(l.next_due.amount)}` : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={l.status === "closed" ? "closed" : l.current_status} daysLate={l.days_late} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/loans/${l.id}`} className="text-emerald-700 font-semibold" data-testid={`loan-view-${l.id}`}>Open</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-slate-100" data-testid="loans-mobile-list">
          {filtered.length === 0 && (
            <div className="px-4 py-10 text-center text-slate-500 text-sm">No loans match.</div>
          )}
          {filtered.map((l) => (
            <Link
              key={l.id}
              to={`/loans/${l.id}`}
              className="block px-4 py-3 active:bg-slate-50"
              data-testid={`loan-card-${l.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{l.customer_name}</div>
                  <div className="text-xs text-slate-500 tabular">{l.customer_mobile}</div>
                  <div className="text-sm mt-1 truncate">{l.product_name}</div>
                  <div className="text-xs text-slate-500">{l.installment_type} · {l.duration}×</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-slate-500">Remaining</div>
                  <div className="font-display font-bold tabular">{fmtPKR(l.remaining_amount)}</div>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <StatusPill status={l.status === "closed" ? "closed" : l.current_status} daysLate={l.days_late} />
                {l.next_due && (
                  <span className="text-xs text-slate-500 tabular truncate">
                    Due {l.next_due.due_date} · {fmtPKR(l.next_due.amount)}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>

      <LoanDialog
        open={open}
        onOpenChange={setOpen}
        onSaved={(created) => {
          load();
          if (created?.id) setCreatedLoan(created);
        }}
      />

      <LoanCreatedDialog
        open={!!createdLoan}
        onOpenChange={(v) => !v && setCreatedLoan(null)}
        loan={createdLoan}
        defaultAction={postCreateAction}
        onGenerateAgreement={() => { setAgreementLoanId(createdLoan?.id); setCreatedLoan(null); }}
        onSkip={() => setCreatedLoan(null)}
      />

      <AgreementDialog
        open={!!agreementLoanId}
        onOpenChange={(v) => !v && setAgreementLoanId(null)}
        loanId={agreementLoanId}
        onLoanActivated={() => { setAgreementLoanId(null); load(); }}
      />
    </div>
  );
}
